import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, KeyRound } from "lucide-react";
import { Logo } from "../components/Logo";
import api, { formatApiError } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain relative flex min-h-screen items-center justify-center overflow-hidden bg-cream px-4 py-16" data-testid="reset-page">
      <img src="/assets/mandala-sun.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-b from-cream via-cream/60 to-cream" />
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-3xl border border-terra/15 bg-white/90 p-8 shadow-card backdrop-blur-md sm:p-10"
      >
        <Link to="/auth" className="mb-8 flex justify-center">
          <Logo size={52} withWordmark={false} />
        </Link>
        {done ? (
          <div className="text-center" data-testid="reset-success">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold-light">
              <Check size={28} className="text-terra" />
            </div>
            <h1 className="mt-4 font-serif text-2xl font-medium text-ink">Mot de passe mis à jour</h1>
            <p className="mt-2 text-sm text-clay">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
            <Link to="/auth" data-testid="reset-login-link" className="mt-6 inline-block rounded-full bg-terra px-6 py-3 text-sm font-semibold text-cream hover:bg-terra-dark">
              Me connecter
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-center font-serif text-3xl font-medium tracking-tight text-ink" data-testid="reset-title">Nouveau mot de passe</h1>
            <p className="mt-2 text-center text-sm text-clay">Choisissez un mot de passe d'au moins 6 caractères.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              {[["Nouveau mot de passe", password, setPassword, "reset-password-input"], ["Confirmez-le", confirm, setConfirm, "reset-confirm-input"]].map(([label, val, setter, testid]) => (
                <div key={testid}>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-terra">{label}</label>
                  <input
                    type="password"
                    value={val}
                    onChange={(e) => setter(e.target.value)}
                    data-testid={testid}
                    required
                    minLength={6}
                    className="w-full rounded-xl border border-terra/20 bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-terra"
                  />
                </div>
              ))}
              {error && <p className="rounded-xl bg-rosedust/20 px-4 py-3 text-sm text-terra-dark" data-testid="reset-error">{error}</p>}
              <button
                type="submit"
                disabled={loading || !token}
                data-testid="reset-submit-button"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-terra px-6 py-3.5 text-sm font-semibold text-cream shadow-boho transition-all duration-300 hover:bg-terra-dark disabled:opacity-60"
              >
                <KeyRound size={15} /> {loading ? "Mise à jour…" : "Enregistrer"}
              </button>
              {!token && <p className="text-center text-xs text-terra-dark">Lien invalide : le jeton est manquant.</p>}
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
